import { format, differenceInMilliseconds } from 'date-fns';

interface ThresholdRes {
  low?: string;
  high?: string;
  value?: string;
}

interface GetEtaValuesModel {
  show: boolean;
  use: boolean;
  rounding: number;
  range: boolean;
  rangeValue: string;
  rangeUnit: string; // 'minutes' or 'hours'
  threshold: boolean;
  lowThreshold: string;
  highThreshold: string;
}

/**
 * Returns a rounding value based on the input string.
 *
 * @param {string} roundingNumber - The input string to determine rounding value.
 * @return {number} The calculated rounding value.
 */
export function getRoundingValue(roundingNumber: string): number {
  switch (roundingNumber) {
    case '1':
      return 5;
    case '2':
      return 10;
    case '3':
      return 15;
    case '4':
      return 30;
    case '5':
      return 60;
    default:
      return 1;
  }
}

/**
 * Returns the range unit based on the given display unit.
 *
 * @param {string} displayUnit - The display unit to be used.
 * @return {string} The range unit based on the given display unit.
 */
export function getRangeUnit(displayUnit: string, t: (text: string) => string): string {
  switch (displayUnit) {
    case '0':
      return t('hours');
    case '1':
      return t('minutes');
    default:
      return t('minutes');
  }
}

export const round = (value: string, round_interval: number) => {
  const date = new Date(`01/01/2018 ` + value);
  const dateMinutes = Math.floor(date.getMinutes() / round_interval);

  const minutesToRound = date.getMinutes() % round_interval;
  const minutesRounded = minutesToRound > round_interval / 2 ? round_interval : 0;

  const minutes = dateMinutes * round_interval + minutesRounded;
  date.setMinutes(minutes);

  return format(date, 'HH:mm');
};

export const range = (time: string, value: string, unit: string) => {
  const date = new Date(`01/01/2018 ` + time);
  const temp = parseInt(value);

  const a = new Date(date);
  const b = new Date(date);

  let durationinmins = temp;
  if (unit === 'hours') {
    durationinmins = durationinmins * 60;
  }

  a.setMinutes(a.getMinutes() - durationinmins);
  b.setMinutes(b.getMinutes() + durationinmins);

  const res = {
    low: format(a, 'HH:mm'),
    high: format(b, 'HH:mm')
  };

  return res;
};

export const threshold = (rangeValues: ThresholdRes, low: string, high: string) => {
  const lowThresh = new Date('01/01/2018 ' + low);
  const highThresh = new Date('01/01/2018 ' + high);

  const res: ThresholdRes = {};

  if (rangeValues.low && rangeValues.high) {
    res.low = rangeValues.low;
    res.high = rangeValues.high;

    const lowDate = new Date('01/01/2018 ' + rangeValues.low);
    const highDate = new Date('01/01/2018 ' + rangeValues.high);

    const lowcheck = differenceInMilliseconds(lowDate, lowThresh);
    const highCheck = differenceInMilliseconds(highDate, highThresh);

    if (lowcheck < 0 && highCheck > 0) {
      res.low = format(lowThresh, 'HH:mm');
      res.high = format(highThresh, 'HH:mm');
    } else if (lowcheck < 0) {
      res.low = format(lowDate.setMilliseconds(lowDate.getMilliseconds() - lowcheck), 'HH:mm');
      res.high = format(highDate.setMilliseconds(highDate.getMilliseconds() - lowcheck), 'HH:mm');
      const highCheck = differenceInMilliseconds(highDate, highThresh);
      if (highCheck > 0) {
        res.high = format(highThresh, 'HH:mm');
      }
    } else if (highCheck > 0) {
      res.high = format(highDate.setMilliseconds(highDate.getMilliseconds() - highCheck), 'HH:mm');
      res.low = format(lowDate.setMilliseconds(highDate.getMilliseconds() - highCheck), 'HH:mm');
      const lowCheck = differenceInMilliseconds(lowDate, lowThresh);
      if (lowCheck < 0) {
        res.low = format(lowThresh, 'HH:mm');
      }
    }
  } else {
    res.value = rangeValues.value;
    const date = new Date('01/01/2018 ' + rangeValues.value);
    const lowcheck = differenceInMilliseconds(date, lowThresh);
    const highCheck = differenceInMilliseconds(date, highThresh);
    if (lowcheck < 0) {
      res.value = format(lowThresh, 'HH:mm');
    } else if (highCheck > 0) {
      res.value = format(highThresh, 'HH:mm');
    }
  }

  return res;
};

export const getEtaValues = (model: GetEtaValuesModel, con: { eta: string }) => {
  let etaValuesResult: ThresholdRes = {};
  if (model && model.show && model.use) {
    const dateNow = new Date();
    const m = new Date('01/01/2018 ' + con.eta);
    const mins = m.getMinutes();
    const hours = m.getHours();

    dateNow.setMinutes(mins);
    dateNow.setHours(hours);
    dateNow.setSeconds(0);
    try {
      if (format(dateNow, 'dd/MM/yyyy') == format(new Date(), 'dd/MM/yyyy')) {
        const momentTime = format(m, 'HH:mm');
        const roundedEta = round(momentTime, model.rounding);
        if (model.range == true) {
          etaValuesResult = range(roundedEta, model.rangeValue, model.rangeUnit);
        } else {
          etaValuesResult.value = roundedEta;
        }

        if (model.threshold == true) {
          etaValuesResult = threshold(etaValuesResult, model.lowThreshold, model.highThreshold);
        }
      } else {
        const diff = differenceInMilliseconds(m, new Date());
        if (diff > 0) {
          etaValuesResult = { value: format(m, 'dd/MM/yyyy') };
        }
      }
    } catch (error) {
      return { value: 'Invalid ETA' };
    }
  } else {
    // ////console.log("dont use eta")
  }

  return etaValuesResult;
};
